import { CommonModule } from '@angular/common'
import { Component, EventEmitter, HostListener, Input, Output, Renderer2 } from '@angular/core'
import { Subject } from 'rxjs'
import { BadgeElement, BadgeElementFieldType, BadgeElementStyle } from '../../../models/badge-variable-elements.model'
import { BadgeEditorService } from '../../../services/badge-editor.service'
import { TemplateLayout } from '../../../types/badge.type'
enum ALIGNMENT {
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right'
}

enum ACTIONS {
  INCREASE_BORDER_RADIUS = 'increaseBorderRadius',
  DECREASE_BORDER_RADIUS = 'decreaseBorderRadius',
  INCREASE_HEIGHT = 'increaseHeight',
  DECREASE_HEIGHT = 'decreaseHeight'
}

const DEFAULT_DISTANCE_FOR_INCREASE_IN_MM = 2
const MINIMUM_COLUMN_HEIGHT_IN_MM = 4
const BADGE_PADDING_IN_MM = 1

@Component({
  selector: 'app-badge-element-tooltip-settings',
  templateUrl: './badge-element-tooltip-settings.component.html',
  styleUrls: ['./badge-element-tooltip-settings.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class BadgeElementTooltipSettingsComponent {
  @Input() columnElement!: HTMLElement
  @Input() variableElements!: BadgeElement[][]
  @Input() public selectedTemplate!: TemplateLayout | null
  @Input() public isQrNumberVisible: boolean = true

  public variableElementsChanges$: Subject<BadgeElement[][]> = new Subject()
  public fieldType!: keyof typeof BadgeElementFieldType
  public badgeElementFieldType = BadgeElementFieldType
  public alignment = ALIGNMENT
  public selectedAlignment: ALIGNMENT = ALIGNMENT.CENTER
  public toggleAlignment: boolean = false
  public isBold!: boolean
  public actions = ACTIONS
  public get shouldShowQrVisibilityToggle(): boolean {
    // In DTCM events we can have 2 QR codes:
    // - Micetribe QR: should keep the visibility (eye) toggle
    // - DTCM QR: should not show the toggle at all
    return this.fieldType === BadgeElementFieldType.QR_CODE
  }

  private _variableElement!: BadgeElement
  private _interval!: any
  private _fieldElement!: HTMLElement

  @Output() variableElementStyleChanged: EventEmitter<BadgeElementStyle> = new EventEmitter<BadgeElementStyle>()
  @Output() qrNumberVisibilityChanged: EventEmitter<boolean> = new EventEmitter<boolean>()

  constructor(
    private _Renderer: Renderer2,
    private _BadgeService: BadgeEditorService
  ) {}

  ngOnInit(): void {
    this._fieldElement = this.columnElement?.children?.[0] as HTMLElement
    this.fieldType = this._fieldElement?.getAttribute('data-element')?.toUpperCase() as keyof typeof BadgeElementFieldType
    this.isBold = this._fieldElement.style.fontWeight === 'bold'
    this.selectedAlignment = this.columnElement.style.justifyContent as ALIGNMENT
    const columnIndex = +this.columnElement.id.split('-')[2] - 1
    const rowIndex = +this.columnElement.id.split('-')[1] - 1
    this._variableElement = this.variableElements[rowIndex][columnIndex]
    // Close the alignment dropdown when mouse leave from the column element
    this._Renderer.listen(this.columnElement, 'mouseleave', () => (this.toggleAlignment = false))
  }

  /**
   * @method _stopAction
   * @description Stopping the continuos action by clearing the interval
   * @returns void
   */
  @HostListener('document:mouseup')
  public stopAction(): void {
    clearInterval(this._interval)
  }

  /**
   * @method handleAlignment
   * @description handle selected alignment and toggle alignment
   * @param alignment accepts ALIGNMENT
   * @param event accepts Event
   * @returns void
   */
  public handleAlignment(event: Event, alignment: ALIGNMENT): void {
    event.stopPropagation()
    this.toggleAlignment = !this.toggleAlignment
    this.selectedAlignment = alignment
    this.columnElement.style.justifyContent = this.selectedAlignment
    this._updateStyle({ 'justify-content': this.columnElement.style.justifyContent })
  }

  /**
   * @method deleteBadgeElement
   * @description delete badge element from variableElements array
   * @param event accepts Event
   * @returns void
   */
  public deleteBadgeElement(event: Event): void {
    event.stopPropagation()
    const columnIndex = +this.columnElement.id.split('-')[2] - 1
    const rowIndex = +this.columnElement.id.split('-')[1] - 1
    // Remove column from variableElements array
    this.variableElements[rowIndex].splice(columnIndex, 1)
    // Remove row if it has no columns
    if (!this.variableElements[rowIndex]?.length) this.variableElements.splice(rowIndex, 1)
    let columnsCountInRow = this._BadgeService.getColumnsCountInRow(rowIndex, this.variableElements)
    if (columnsCountInRow === 1) {
      const elements = this.variableElements[rowIndex]
      this.variableElements.splice(rowIndex, 1)
      let startRowIndex = rowIndex
      let columnsCount = elements.length
      while (columnsCount > 0) {
        const element = elements.shift() as BadgeElement
        element.columnContainerIndex = 0
        this.variableElements.splice(startRowIndex, 0, [element])
        startRowIndex++
        columnsCount--
      }
    }
    this.variableElementsChanges$.next(this.variableElements)
  }

  /**
   * @method increaseBorderRadius
   * @description increase border radius of the element
   * @returns void
   */
  public increaseBorderRadius(): void {
    const borderRadius = parseInt(this._fieldElement.style.borderRadius)
    if (borderRadius === 50) return
    this._fieldElement.style.borderRadius = `${borderRadius + 1}%`
    this._updateStyle({ 'border-radius': this._fieldElement.style.borderRadius })
  }

  /**
   * @method decreaseBorderRadius
   * @description decrease border radius of the element
   * @returns void
   */
  public decreaseBorderRadius(): void {
    const borderRadius = parseInt(this._fieldElement.style.borderRadius)
    if (borderRadius === 0) return
    this._fieldElement.style.borderRadius = `${borderRadius - 1}%`
    this._updateStyle({ 'border-radius': this._fieldElement.style.borderRadius })
  }

  /**
   * @method continuousAction
   * @description perform an continuos action on the element
   * @param event accepts Event
   * @param action accepts ACTIONS enum
   * @returns void
   */
  public continuousAction(event: Event, action: ACTIONS): void {
    event.stopPropagation()
    this._interval = setInterval(() => this?.[action](), 200)
  }

  /**
   * @method toggleTextBoldness
   * @description Toggle between text bold and normal, then emit the changed style
   * @param event accepts Event
   * @returns void
   */
  public toggleTextBoldness(event: Event): void {
    event.stopPropagation()
    this.isBold = !this.isBold
    this._fieldElement.style.fontWeight = this.isBold ? 'bold' : 'normal'
    this._updateStyle({ 'font-weight': this._fieldElement.style.fontWeight })
  }

  /**
   * @method toggleQrNumberVisibility
   * @description Show/Hide QR number text and emit updated style
   * @param event accepts Event
   * @returns void
   */
  public toggleQrNumberVisibility(event: Event): void {
    event.stopPropagation()
    if (!this.shouldShowQrVisibilityToggle) return
    this.qrNumberVisibilityChanged.emit(!this.isQrNumberVisible)
  }

  /**
   * @method increaseHeight
   * @description Increase element height
   * @returns void
   */
  public increaseHeight(): void {
    const height = parseFloat(this.columnElement.style.height)
    const heightTextElementInColumn = this.columnElement.querySelector('.height-element span') as HTMLElement
    const heightTextElementInMM = parseInt(heightTextElementInColumn.innerText)
    const spaceInPX = (DEFAULT_DISTANCE_FOR_INCREASE_IN_MM * this._BadgeService.getElementHeight('#badge')) / this.selectedTemplate!.height
    const hasTopAndDownSpace =
      Math.round(this._BadgeService.getElementHeight('#badge')) -
        Math.round(this._BadgeService.getElementHeight('#badge-content-container')) -
        this._BadgeService.convertMMToPX(BADGE_PADDING_IN_MM) >=
      spaceInPX
    const containerHasTopAndDownSpace = this._BadgeService.containerHasEnoughSpace(this.columnElement.parentElement as HTMLElement, spaceInPX)
    if (!hasTopAndDownSpace && !containerHasTopAndDownSpace) return
    this.columnElement.style.height = `${height + spaceInPX}px`
    const rowIndex = +this.columnElement.id.split('-')[1] - 1
    this._BadgeService.resizeTextValueToFitParent(rowIndex)
    this._handleBadgeMargin(spaceInPX)
    const heightInMM = heightTextElementInMM + DEFAULT_DISTANCE_FOR_INCREASE_IN_MM
    const heightTextElement = this.columnElement.querySelector('.height-element span') as HTMLElement
    heightTextElement.innerText = heightInMM.toString()
    this._updateStyle({ height: `${heightInMM}mm` })
  }

  /**
   * @method decreaseHeight
   * @description Decrease element height
   * @returns void
   */
  public decreaseHeight(): void {
    const height = parseFloat(this.columnElement.style.height)
    const heightTextElementInColumn = this.columnElement.querySelector('.height-element span') as HTMLElement
    const heightTextElementInMM = parseInt(heightTextElementInColumn.innerText)
    if (heightTextElementInMM <= MINIMUM_COLUMN_HEIGHT_IN_MM) return
    const spaceInPX = (DEFAULT_DISTANCE_FOR_INCREASE_IN_MM * this._BadgeService.getElementHeight('#badge')) / this.selectedTemplate!.height
    this.columnElement.style.height = `${height - spaceInPX}px`
    const rowIndex = +this.columnElement.id.split('-')[1] - 1
    this._BadgeService.resizeTextValueToFitParent(rowIndex)
    const heightInMM = heightTextElementInMM - DEFAULT_DISTANCE_FOR_INCREASE_IN_MM
    const heightTextElement = this.columnElement.querySelector('.height-element span') as HTMLElement
    heightTextElement.innerText = heightInMM.toString()
    this._updateStyle({ height: `${heightInMM}mm` })
  }

  /**
   * @method _handleBadgeMargin
   * @description Handle badge margin when the content is push to top or bottom
   * @param space accepts number
   * @returns void
   */
  private _handleBadgeMargin(space: number): void {
    const badgeContentContainer = document.getElementById('badge-content-container')
    if (!parseFloat(badgeContentContainer?.style.marginBottom as string) || !badgeContentContainer?.style?.marginBottom) return
    const marginBottom = window.getComputedStyle(badgeContentContainer).marginBottom
    const isExceeded = Math.abs(parseFloat(marginBottom)) + this._BadgeService.getElementHeight('#badge-content-container') >= this._BadgeService.getElementHeight('#badge') - 5
    if (isExceeded) {
      const margin = parseInt(marginBottom) > 0 ? parseFloat(marginBottom) - space : parseFloat(marginBottom) + space
      const percentage = (margin / badgeContentContainer.clientWidth) * 100
      badgeContentContainer.style.marginBottom = `${percentage}%`
    }
  }

  /**
   * @method _updateStyle
   * @description Update the variable element style
   * @param newStyle accepts BadgeElementStyle
   * @returns void
   */
  private _updateStyle(newStyle: BadgeElementStyle): void {
    const style: BadgeElementStyle = {
      ...this._variableElement?.style,
      ...newStyle
    }
    this.variableElementStyleChanged.emit(style)
  }
}
